import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, Star, Trash2, Check, Plus, Loader2 } from "lucide-react";

import { categoryLabel } from "@/lib/segments";

function summarize(c = {}) {
  const parts = [];
  if (c.search) parts.push(`"${c.search}"`);
  if (c.city) parts.push(c.city);
  if (c.category) parts.push(categoryLabel(c.category));
  if (c.source) parts.push(c.source);
  if (c.only_new) parts.push("novos 7d");
  return parts.length ? parts.join(" · ") : "Todos os anúncios";
}

function isEmptyCriteria(c = {}) {
  return !c.search && !c.source && !c.city && !c.category && !c.only_new;
}

export default function SavedFilters({ currentCriteria, onApply }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/filters");
      setItems(data.items || []);
      setDefaultId(data.default_id || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const save = async () => {
    const nm = name.trim();
    if (!nm) { toast.error("Dê um nome ao filtro"); return; }
    setSaving(true);
    try {
      await api.post("/me/filters", {
        name: nm, criteria: currentCriteria, make_default: makeDefault,
      });
      toast.success(`Filtro "${nm}" guardado`);
      setName("");
      setMakeDefault(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar filtro");
    } finally {
      setSaving(false);
    }
  };

  const apply = (f) => {
    onApply?.(f.criteria || {});
    toast.info(`Filtro "${f.name}" aplicado`);
    setOpen(false);
  };

  const toggleDefault = async (f) => {
    const willBe = defaultId !== f.id;
    try {
      await api.put(`/me/filters/${f.id}`, { is_default: willBe });
      await load();
      toast.success(willBe ? `"${f.name}" definido como predefinido` : "Predefinição removida");
    } catch (e) {
      toast.error("Erro ao atualizar");
    }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/me/filters/${f.id}`);
      await load();
      toast.success("Filtro eliminado");
    } catch (e) {
      toast.error("Erro ao eliminar");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="saved-filters-button"
        onClick={() => setOpen((o) => !o)}
        className={`px-3 py-2 text-sm border transition-colors inline-flex items-center gap-1.5 ${
          defaultId ? "border-[#c8aa6e] text-[#c8aa6e] bg-[#c8aa6e]/10" : "border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]"
        }`}
        title="Filtros guardados"
      >
        <Bookmark className="w-4 h-4" /> Filtros guardados
        {items.length > 0 && <span className="text-[11px] font-mono">({items.length})</span>}
      </button>

      {open && (
        <div
          data-testid="saved-filters-panel"
          className="absolute left-0 sm:left-auto sm:right-0 z-50 mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-[#18181b] border border-[#3f3f46] shadow-xl p-3 text-sm"
        >
          <div className="label-tiny mb-2">Os meus filtros</div>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-[#71717a] py-3">
              <Loader2 className="w-3.5 h-3.5 spin" /> A carregar…
            </div>
          ) : items.length === 0 ? (
            <div className="text-xs text-[#71717a] py-2">Ainda não guardou filtros.</div>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {items.map((f) => (
                <div key={f.id} data-testid={`saved-filter-${f.id}`} className="bg-[#09090b] border border-[#27272a] px-2.5 py-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`saved-filter-apply-${f.id}`}
                      onClick={() => apply(f)}
                      className="flex-1 min-w-0 text-left"
                      title="Aplicar filtro"
                    >
                      <div className="text-[#fafafa] truncate flex items-center gap-1.5">
                        {f.name}
                        {f.is_default && (
                          <span className="text-[10px] uppercase tracking-wider text-[#c8aa6e] border border-[#c8aa6e]/40 px-1">predef.</span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#71717a] truncate">{summarize(f.criteria)}</div>
                    </button>
                    <button
                      type="button"
                      data-testid={`saved-filter-default-${f.id}`}
                      onClick={() => toggleDefault(f)}
                      title={f.is_default ? "Remover predefinição" : "Definir como predefinido"}
                      className={`p-1 ${f.is_default ? "text-[#c8aa6e]" : "text-[#71717a] hover:text-[#c8aa6e]"}`}
                    >
                      <Star className={`w-4 h-4 ${f.is_default ? "fill-[#c8aa6e]" : ""}`} />
                    </button>
                    <button
                      type="button"
                      data-testid={`saved-filter-delete-${f.id}`}
                      onClick={() => remove(f)}
                      title="Eliminar"
                      className="p-1 text-[#71717a] hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-[#27272a] mt-3 pt-3">
            <div className="label-tiny mb-1.5">Guardar filtro atual</div>
            <div className="text-[11px] text-[#71717a] mb-2 truncate">{summarize(currentCriteria)}</div>
            <input
              data-testid="saved-filter-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              placeholder="Nome do filtro (ex.: Lisboa novas)"
              className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-2.5 py-1.5 text-sm mb-2"
            />
            <label className="flex items-center gap-2 text-xs text-[#a1a1aa] mb-2 cursor-pointer select-none">
              <button
                type="button"
                data-testid="saved-filter-make-default"
                onClick={() => setMakeDefault((v) => !v)}
                className={`w-4 h-4 border flex items-center justify-center ${makeDefault ? "border-[#c8aa6e] bg-[#c8aa6e]/20" : "border-[#3f3f46]"}`}
              >
                {makeDefault && <Check className="w-3 h-3 text-[#c8aa6e]" />}
              </button>
              Aplicar automaticamente ao entrar (predefinido)
            </label>
            <button
              type="button"
              data-testid="saved-filter-save"
              onClick={save}
              disabled={saving}
              className="w-full bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-3 py-2 font-medium text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 spin" /> : <Plus className="w-4 h-4" />} Guardar filtro
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
